// The user's saved audio mix (modSettings.audioLevels), shared by the library
// Volume Controls menu and the in-room Poppy menu.

import { useCallback, useState } from 'react';
import { useAuth } from './useAuth';
import { useUserSave } from './useUserSave';
import { DEFAULT_VOLUMES } from './useMediaVolumeManager';
import type { ModSettings } from '@airhead-games/shared/types';

export type AudioLevels = ModSettings['audioLevels'];

export type AudioLevelKey = keyof AudioLevels;

const GUEST_AUDIO_LEVELS: AudioLevels = {
  speechVolume: DEFAULT_VOLUMES.speechVolume,
  musicVolume: DEFAULT_VOLUMES.musicVolume,
  sfxVolume: DEFAULT_VOLUMES.sfxVolume,
};

/**
 * Mirror of the saved mix. Both volume surfaces write through this; nothing here
 * drives live audio, so a failed save rolls the mirror back without touching
 * playback. Signed-out callers see defaults and cannot save.
 */
export function useAudioLevels() {
  const { user } = useAuth();
  const [audioLevels, setAudioLevels] = useState<AudioLevels>(
    user?.modSettings.audioLevels ?? GUEST_AUDIO_LEVELS
  );

  const { markDirty } = useUserSave({
    value: audioLevels,
    setValue: setAudioLevels,
    toUpdate: (value) => ({ modSettings: { audioLevels: value } }),
    fromUser: (nextUser) => nextUser.modSettings.audioLevels,
    errorMessage: 'Unable to save your audio levels.',
  });

  const setLevel = useCallback(
    (key: AudioLevelKey, value: number) => {
      if (!user) {
        return;
      }
      setAudioLevels((current) => ({ ...current, [key]: value }));
      markDirty();
    },
    [markDirty, user]
  );

  return { audioLevels, setLevel };
}
